import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { CheckCircle2, CheckSquare, ChevronDown, ChevronRight, ExternalLink, Lock, Pencil, Repeat, Route, Sparkles } from 'lucide-react'
import { Button } from '../ui/Button'
import { Timestamp } from '../ui/Timestamp'
import { useToast } from '../ui/Toast'
import { useBranding } from '../../hooks/useBranding'
import { JourneyPreviewModal, loadDraftFromStorage, clearDraftFromStorage, formatDraftAge } from '../journey'
import type { BreakdownStep, JourneyPlan } from '../../types/journey'
import { SkillService, type Skill, type JourneyStep, type SkillBoardCard } from '../../services/skillService'
import { scheduleWords } from '../../utils/habitWords'
import { habitScheduleInput } from '../../utils/skillWords'
import { dueLine, clockWithZone } from '../../utils/taskForm'
import { fromApiStep, stepFieldsFor, toApiSteps } from '../journey/stepKinds'

/**
 * What builds a skill (Skills UX pass 2026-09-06; owner: "a skill is made up
 * of tasks and habits, so the step by step thingy shows the different items
 * we will be tracking"). The learning path first — each step is a task or a
 * habit, chosen in the review; a habit step is created under the skill and
 * carries on, a task step is the one thing to do and finishing it unlocks the
 * next (the journey engine) — then "Also building it": the habits and other
 * tasks under the skill that are not journey steps. With no journey: "Plan the
 * journey" asks the persona once (typed steps, a habit with a suggested
 * schedule) and shows the result for review before anything is saved; "Write
 * my own steps" opens the same review empty. The first task step is
 * highlighted as the place to start. The owner's word is "journey" — it is the
 * engine's word too, and it says there is something to look forward to.
 */
const DRAFT_KEY = 'skill_journey_draft'

interface Props {
  skill: Skill
  card: SkillBoardCard | null
  live: boolean
  headingClass: string
  tz?: string | null
  onPlan: (plan: JourneyPlan | null) => void
  refreshKey?: string | number | null
  autoStart?: boolean
  /** True when the page has no other next move (no next step, no review
   *  banner): "Plan the journey" is then its one filled button (rule 3). */
  leads?: boolean
}

export function SkillPath({ skill, card, live, headingClass, tz, onPlan, refreshKey, autoStart = false, leads = false }: Props) {
  const toast = useToast()
  const { assistant: personaName } = useBranding()
  const svc = useRef(new SkillService()).current
  const skillId = Number(skill.id)
  const [plan, setPlan] = useState<JourneyPlan | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [open, setOpen] = useState(true)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [preview, setPreview] = useState<BreakdownStep[] | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalLoading, setModalLoading] = useState(false)
  const [editing, setEditing] = useState(false)
  const [completedCount, setCompletedCount] = useState(0)
  const [accepting, setAccepting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [draftAge, setDraftAge] = useState<string | null>(null)
  const [resumed, setResumed] = useState(false)
  const autoStarted = useRef(false)

  const load = useCallback(async () => {
    try {
      const p = await svc.getJourneyPlan(skillId)
      const next = (p as unknown as JourneyPlan) || null
      setPlan(next); onPlan(next)
    } catch { setPlan(null); onPlan(null) }
    finally { setLoaded(true) }
  }, [svc, skillId, onPlan])
  useEffect(() => { void load() }, [load, refreshKey])
  useEffect(() => {
    const d = loadDraftFromStorage(DRAFT_KEY, skillId)
    setDraftAge(d ? formatDraftAge(d.savedAt) : null)
  }, [skillId, modalOpen])

  const openPreview = (steps: BreakdownStep[] | null, opts: { loading?: boolean; resumed?: boolean } = {}) => {
    setPreview(steps); setEditing(false); setCompletedCount(0); setResumed(Boolean(opts.resumed)); setModalLoading(Boolean(opts.loading)); setModalOpen(true)
  }

  // One call maps the whole path; the first task step is then the place to start.
  const mapPath = async () => {
    setErr(null); setBusy(true)
    openPreview(null, { loading: true })
    try {
      const r = await svc.previewBreakdown(skillId)
      setPreview(r.steps.map((s) => fromApiStep(s, ['task', 'habit'])))
    } catch (e) {
      setModalOpen(false)
      setErr(e instanceof Error ? e.message : 'Could not plan the journey just now')
    } finally { setBusy(false); setModalLoading(false) }
  }

  useEffect(() => {
    if (!autoStart || autoStarted.current || !loaded || plan) return
    autoStarted.current = true
    void mapPath()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, loaded, plan])

  const resumeDraft = () => {
    const d = loadDraftFromStorage(DRAFT_KEY, skillId)
    if (d) openPreview(d.steps, { resumed: true })
  }
  const discardDraft = () => { clearDraftFromStorage(DRAFT_KEY, skillId); setDraftAge(null); setResumed(false); setModalOpen(false); setPreview(null) }

  const accept = async (steps: BreakdownStep[]) => {
    setAccepting(true)
    try {
      const r = await svc.acceptBreakdown(skillId, toApiSteps(steps) as Parameters<typeof svc.acceptBreakdown>[1])
      clearDraftFromStorage(DRAFT_KEY, skillId); setDraftAge(null)
      setModalOpen(false); setPreview(null)
      await load()
      const habits = r.created_habits?.length || 0
      if (r.created_task) toast.toast({ variant: 'success', title: 'Journey started', description: `Start here: ${r.created_task.title}${habits ? ` · ${habits} habit${habits === 1 ? '' : 's'} set up alongside` : ''}`, action: { label: 'Open the step', onClick: () => { window.location.assign(`/tasks/${r.created_task!.id}`) } } })
      else toast.success('Journey saved', habits ? `${habits} habit${habits === 1 ? '' : 's'} set up under this skill.` : 'The first step is on its way.')
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not start the journey')
    } finally { setAccepting(false) }
  }

  const edit = () => {
    if (!plan) return
    // step_ref and what each step waits on come along: dropping them here would
    // quietly flatten the journey the moment someone opened the editor.
    const refById = new Map(plan.steps.map((s) => [s.step_id, s.step_ref || s.step_id]))
    setPreview(plan.steps.map((s) => ({
      step_id: s.step_id,
      step_ref: s.step_ref || s.step_id,
      title: s.title,
      description: s.description || '',
      estimated_duration_minutes: s.estimated_duration_minutes,
      order: s.order,
      child_type: (s.child_type as 'task' | 'habit') || 'task',
      prerequisite_step_refs: (s.prerequisite_step_ids || []).map((id) => refById.get(id) || id),
      demonstrates_level: s.demonstrates_level ?? null,
    })))
    // finished steps and habits already made keep their place; the review shows them locked
    setCompletedCount(plan.steps.filter((s) => s.status === 'completed' || s.habit_id != null).length)
    setEditing(true); setResumed(false); setModalLoading(false); setModalOpen(true)
  }

  const saveEdit = async (steps: BreakdownStep[]) => {
    setSaving(true)
    try {
      await svc.updateJourneyPlan(skillId, toApiSteps(steps) as unknown as JourneyStep[])
      setModalOpen(false); setPreview(null); setEditing(false)
      await load()
      toast.success('Journey updated')
    } catch (e) {
      toast.error('Could not save the journey', e instanceof Error ? e.message : 'Please try again.')
    } finally { setSaving(false) }
  }

  const steps = plan?.steps || []
  const done = steps.filter((s) => s.status === 'completed').length
  const startHereId = steps.find((s) => s.status === 'unlocked' && s.child_type !== 'habit')?.step_id || null
  // What a step is still waiting for, named so the wait is never a mystery. Only
  // the outstanding ones — a prerequisite already done is not a wait.
  const stepById = new Map(steps.map((s) => [s.step_id, s]))
  const waitingOn = (step: typeof steps[number]) =>
    (step.prerequisite_step_ids || [])
      .map((id) => stepById.get(id))
      .filter((s) => s && s.status !== 'completed')
      .map((s) => s!.title)
  const pathTaskIds = new Set(steps.map((s) => s.task_id).filter((x): x is number => x != null))
  const pathHabitIds = new Set(steps.map((s) => s.habit_id).filter((x): x is number => x != null))
  const habitById = new Map((card?.items.habits || []).map((h) => [h.id, h]))
  const otherHabits = (card?.items.habits || []).filter((h) => !pathHabitIds.has(h.id))
  const otherTasks = (card?.items.tasks || []).filter((t) => !pathTaskIds.has(t.id))
  const tracked = otherHabits.length + otherTasks.length

  const habitLine = (h: NonNullable<ReturnType<typeof habitById.get>>) => {
    const doneToday = h.card?.today?.outcome === 'done'
    const words = scheduleWords(habitScheduleInput(h))
    return (
      <>
        {doneToday && h.card?.today?.at ? <>Done today <Timestamp value={h.card.today.at} mode="time" /></> : words || 'No schedule yet · set one on the habit'}
        {h.live && (h.card?.streak || 0) >= 2 ? <span className="text-ok"> · {h.card!.streak} in a row</span> : null}
        {h.live && !doneToday && h.card?.next ? <> · next <Timestamp value={h.card.next.at || `${h.card.next.date}T12:00:00`} mode={h.card.next.at ? 'datetime' : 'date'} /></> : null}
      </>
    )
  }

  return (
    <section data-testid="skill-path">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="flex items-center gap-1.5 text-left">
          {open ? <ChevronDown className="h-3.5 w-3.5 text-muted" /> : <ChevronRight className="h-3.5 w-3.5 text-muted" />}
          <h3 className={headingClass}><Route className="h-3.5 w-3.5 text-brand" /> Journey</h3>
          {plan && <span className="text-app-micro text-muted" data-testid="skill-path-count">{plan.status === 'completed' ? 'complete' : `${done} of ${steps.length}`}</span>}
          {tracked > 0 && <span className="text-app-micro text-muted" data-testid="skill-path-tracked">· {tracked} more tracked</span>}
        </button>
        {plan && plan.status !== 'completed' && live && (
          <button type="button" onClick={edit} className="ml-auto inline-flex items-center gap-1 text-app-micro font-medium text-brand hover:underline" data-testid="skill-path-edit"><Pencil className="h-3 w-3" /> Edit journey</button>
        )}
      </div>

      {open && plan && (
        <ol className="mt-2 space-y-1.5" data-testid="skill-path-steps">
          {steps.map((step, idx) => {
            const isHabit = step.child_type === 'habit'
            const isDone = step.status === 'completed'
            const active = step.status === 'unlocked'
            const startHere = step.step_id === startHereId
            const item = active && !isHabit && step.task_id ? (card?.items.tasks || []).find((t) => t.id === step.task_id) : null
            const habit = isHabit && step.habit_id != null ? habitById.get(step.habit_id) : undefined
            const slot = item?.next
            const waits = step.status === 'locked' ? waitingOn(step) : []
            return (
              <li key={step.step_id} className={`flex items-start gap-2 rounded-md text-sm ${startHere ? 'bg-brand-subtle/60 -mx-2 px-2 py-1.5' : ''}`} data-testid="skill-path-step" data-kind={isHabit ? 'habit' : 'task'} data-start-here={startHere || undefined}>
                <span className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-app-micro font-bold ${isDone ? 'bg-ok text-on-brand' : startHere ? 'bg-brand text-on-brand ring-2 ring-brand/30' : active ? (isHabit ? 'bg-ok/20 text-ok' : 'bg-brand text-on-brand') : 'bg-surface-alt text-muted'}`}>
                  {isDone ? <CheckCircle2 className="h-3 w-3" /> : isHabit ? <Repeat className="h-3 w-3" /> : step.status === 'locked' ? <Lock className="h-3 w-3" /> : idx + 1}
                </span>
                <span className="min-w-0">
                  <span className={isDone || active ? 'text-primary' : 'text-muted'}>{step.title}</span>
                  <span className="ml-2 rounded bg-surface-alt px-1 py-0.5 text-app-nano font-medium uppercase tracking-wider text-muted" data-testid="skill-path-kind">{isHabit ? 'habit' : 'task'}</span>
                  {startHere && <span className="ml-2 text-app-micro font-medium text-brand" data-testid="skill-path-start-here">start here</span>}
                  {step.demonstrates_level && <span className="ml-2 text-app-micro text-secondary" data-testid="skill-path-shows-level">shows you are at {step.demonstrates_level}</span>}
                  {active && !isHabit && step.task_id && <Link to={`/tasks/${step.task_id}`} className="ml-2 inline-flex items-center gap-1 text-app-micro text-brand hover:underline" data-testid="skill-path-open-step"><ExternalLink className="h-3 w-3" /> open</Link>}
                  {isHabit && step.habit_id != null && <Link to={`/habits/${step.habit_id}`} className="ml-2 inline-flex items-center gap-1 text-app-micro text-brand hover:underline" data-testid="skill-path-open-habit"><ExternalLink className="h-3 w-3" /> open</Link>}
                  {isDone && step.completed_at && <span className="ml-2 text-app-micro text-muted">done <Timestamp value={step.completed_at} mode="date" /></span>}
                  {isHabit && active && (
                    <span className="block text-app-micro text-secondary" data-testid="skill-path-habit-line">{habit ? habitLine(habit) : 'Ongoing'}</span>
                  )}
                  {!isHabit && active && (slot?.date || item?.due_date) && (
                    <span className="block text-app-micro text-secondary" data-testid="skill-path-step-when">
                      {slot?.date ? <>On the calendar <Timestamp value={`${slot.date}T12:00:00`} mode="date" />{slot.time ? ` at ${clockWithZone(slot.time, tz)}` : ''}</> : dueLine(item!)}
                    </span>
                  )}
                  {!isHabit && active && step.description && !slot?.date && !item?.due_date && <span className="block text-app-micro text-secondary">{step.description}</span>}
                  {step.status === 'locked' && waits.length > 0 && (
                    <span className="block text-app-micro text-muted" data-testid="skill-path-waits-on">
                      {isHabit ? 'Set up after' : 'Starts after'} {waits.join(' and ')}
                    </span>
                  )}
                  {isHabit && step.status === 'locked' && waits.length === 0 && <span className="block text-app-micro text-muted">Set up when the journey reaches it</span>}
                </span>
              </li>
            )
          })}
        </ol>
      )}

      {open && loaded && !plan && (
        <div className="mt-2 space-y-2" data-testid="skill-path-empty">
          <p className="text-app-meta text-secondary">No journey yet. {live ? `${personaName} can plan one — you review it, keep or change which steps are tasks and which are habits, then start. Or write the steps yourself.` : 'It can be planned once this is picked back up.'}</p>
          {live && draftAge && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-brand/20 bg-brand-subtle p-2 text-app-meta">
              <span className="text-primary">You have an unsaved journey, saved {draftAge}.</span>
              <Button size="xs" onClick={resumeDraft} data-testid="skill-path-resume">Resume it</Button>
              <button type="button" onClick={discardDraft} className="text-app-micro text-muted underline decoration-dotted hover:text-secondary">Discard</button>
            </div>
          )}
          {live && (
            <div className="flex flex-wrap items-center gap-2">
              <Button size="xs" variant={leads && !draftAge ? 'default' : 'outline'} className={leads && !draftAge ? '' : 'hover:bg-brand-subtle'} disabled={busy} onClick={() => void mapPath()} data-testid="skill-path-map"><Sparkles className="mr-1.5 h-3.5 w-3.5" />Plan the journey</Button>
              <button type="button" onClick={() => openPreview([])} className="text-app-micro font-medium text-brand hover:underline" data-testid="skill-path-own">Write my own steps</button>
            </div>
          )}
          {busy && <p className="text-app-micro text-muted">Working on it…</p>}
        </div>
      )}

      {open && tracked > 0 && (
        <div className="mt-3" data-testid="skill-path-items">
          <p className="text-app-micro font-medium uppercase tracking-widest text-muted">{plan ? 'Also building it' : 'Building it'}</p>
          <ul className="mt-1.5 space-y-1.5">
            {otherHabits.map((h) => {
              const doneToday = h.card?.today?.outcome === 'done'
              return (
                <li key={`h${h.id}`} className="flex items-start gap-2 text-sm" data-testid="skill-item-habit" data-live={h.live}>
                  <span className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${doneToday ? 'bg-ok text-on-brand' : 'bg-surface-alt text-secondary'}`} aria-hidden="true">
                    {doneToday ? <CheckCircle2 className="h-3 w-3" /> : <Repeat className="h-3 w-3" />}
                  </span>
                  <span className="min-w-0">
                    <Link to={`/habits/${h.id}`} className={`hover:text-brand hover:underline ${h.live ? 'text-primary' : 'text-muted'}`}>{h.title}</Link>
                    <span className="ml-2 text-app-micro text-muted">habit{!h.live ? ' · paused' : ''}</span>
                    <span className="block text-app-micro text-secondary">{habitLine(h)}</span>
                  </span>
                </li>
              )
            })}
            {otherTasks.map((t) => (
              <li key={`t${t.id}`} className="flex items-start gap-2 text-sm" data-testid="skill-item-task">
                <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-surface-alt text-secondary" aria-hidden="true"><CheckSquare className="h-3 w-3" /></span>
                <span className="min-w-0">
                  <Link to={`/tasks/${t.id}`} className="text-primary hover:text-brand hover:underline">{t.title}</Link>
                  <span className="ml-2 text-app-micro text-muted">task{t.is_recurring ? ' · repeats' : ''}</span>
                  <span className="block text-app-micro text-secondary">
                    {t.next?.date ? <>On the calendar <Timestamp value={`${t.next.date}T12:00:00`} mode="date" />{t.next.time ? ` at ${clockWithZone(t.next.time, tz)}` : ''}</> : (dueLine(t) || (t.status === 'in_progress' ? 'In progress' : 'Not planned yet'))}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {err && <p className="mt-2 text-xs text-bad" role="alert">{err}</p>}

      <JourneyPreviewModal
        open={modalOpen}
        onOpenChange={(o) => { if (!o) { setModalOpen(false); setPreview(null); setEditing(false) } }}
        title={editing ? 'Edit the journey' : `The journey for ${skill.title}`}
        description={editing ? 'Change what is not done yet. Finished steps and habits already set up stay where they are.' : 'Change the steps until they read like yours. Each step is a task (done once, by when you say) or a habit (comes round on a schedule). Then start: the first task becomes the place to begin, and habits are set up alongside.'}
        steps={preview}
        loading={modalLoading}
        onStepsChange={setPreview}
        onAccept={accept}
        onSaveEdit={saveEdit}
        isEditingActive={editing}
        completedCount={completedCount}
        accepting={accepting}
        savingEdit={saving}
        draftKey={DRAFT_KEY}
        entityId={skillId}
        entityTitle={skill.title}
        resumedFromDraft={resumed}
        onDiscardDraft={discardDraft}
        showStepTypeSelector
        stepTypes={['task', 'habit']}
        renderStepExtra={stepFieldsFor}
      />
    </section>
  )
}

export default SkillPath
